import { ReasonCode } from "types/landlord/reports";
import { isAmountNotVerified } from "./fraudRisk";

export type DocumentProblem = {
  label: string;
  description?: string;
};

export const NAME_MISMATCH_LABEL = "Name mismatch";

const DOCUMENT_PROBLEMS = {
  Unprocessable: { label: "Rejected" },
  TypeMismatch: { label: "Rejected" },
  NoAmount: { label: "No income amount found" },
  NoTransactions: { label: "No transactions selected" },
  NameMismatch: { label: NAME_MISMATCH_LABEL }
} satisfies Record<string, DocumentProblem>;

const UNPROCESSABLE_DETECT_STATUSES = ["unable_to_process", "unsupported"];

type DocumentProblemSource = {
  verifyType?: string;
  hasTransactions?: boolean;
  detect_status?: string;
  doc_info?: {
    status?: string;
    rejection_reason?: string;
    rejection_reason_description?: string;
  } | null;
  additional_data?: { type_mismatch?: boolean | null } | null;
  name_mismatch?: unknown;
};

const toStatus = (value?: string) => value?.toLowerCase() ?? "";

export const resolveDocumentProblems = (row: unknown): DocumentProblem[] => {
  const source = (row ?? {}) as DocumentProblemSource;
  const problems: DocumentProblem[] = [];

  const isUnprocessable =
    UNPROCESSABLE_DETECT_STATUSES.includes(toStatus(source.detect_status)) ||
    toStatus(source.doc_info?.status) === "rejected";

  if (isUnprocessable) {
    const reason =
      source.doc_info?.rejection_reason_description ??
      source.doc_info?.rejection_reason;

    problems.push({
      ...DOCUMENT_PROBLEMS.Unprocessable,
      ...(reason
        ? { description: `Uploaded document rejected: ${reason}` }
        : {})
    });
  }

  if (source.additional_data?.type_mismatch === true)
    problems.push(DOCUMENT_PROBLEMS.TypeMismatch);

  if (isAmountNotVerified(row)) problems.push(DOCUMENT_PROBLEMS.NoAmount);

  if (source.hasTransactions === false)
    problems.push(DOCUMENT_PROBLEMS.NoTransactions);

  if (source.name_mismatch) problems.push(DOCUMENT_PROBLEMS.NameMismatch);

  return problems;
};

export const toFraudRiskLines = (
  reasonCodes?: ReasonCode[],
  summary?: string | null
): string[] => [
  ...(reasonCodes ?? [])
    .filter((item) => Boolean(item?.description))
    .map((item) =>
      item.confidence
        ? `${item.description}: ${item.confidence} confidence`
        : String(item.description)
    ),
  ...(summary ? [summary] : [])
];
